import {SerumUnit, Sex, type GfrMethodType, type SerumUnitType} from '@cypro/engine/database/schema'
import {cockcroftGault, GFR_PLAUSIBLE_MAX, jelliffe, urinary24h} from '@cypro/engine/utils'
import {getAge, isDefined} from '@cypro/ui/utils'
import {Decimal} from 'decimal.js'

// CY-348, agreed with the developer on 27 Oct 2026, pending clinical sign-off.
// Plausible serum creatinine: 0.1-20 mg/dL, written out per unit (x 88.4) so the limits are exact.
export const SERUM_CREATININE_RANGE: Record<SerumUnitType, {min: number; max: number}> = {
  [SerumUnit.enum.ml_dl]: {min: 0.1, max: 20},
  [SerumUnit.enum.umol_l]: {min: 8.84, max: 1768},
}

export function isSerumCreatinineInRange(value: number, unit: SerumUnitType): boolean {
  const {min, max} = SERUM_CREATININE_RANGE[unit]
  return value >= min && value <= max
}

// 1 mg/dL = 88.4 µmol/L (as mgDlToUmolL in the engine); Decimal keeps 0.8 mg/dL at exactly 70.72 µmol/L
const UMOL_L_PER_MG_DL = 88.4

/** The same serum creatinine expressed in `to`, for when the user switches the unit (CY-348). */
export function convertSerumCreatinine(value: number, to: SerumUnitType): number {
  const amount = new Decimal(value)
  return (to === SerumUnit.enum.umol_l ? amount.mul(UMOL_L_PER_MG_DL) : amount.div(UMOL_L_PER_MG_DL)).toNumber()
}

// A GFR above this is shown as implausible instead of Normal, and cannot be saved (CY-348).
export {GFR_PLAUSIBLE_MAX}

export function gfrLevel(gfrValue: number) {
  if (gfrValue > GFR_PLAUSIBLE_MAX) return 'implausible'
  if (gfrValue >= 90) return 'normal'
  if (gfrValue >= 60) return 'mild'
  if (gfrValue >= 45) return 'mild_moderate'
  if (gfrValue >= 30) return 'moderate_severe'
  if (gfrValue >= 15) return 'severe'
  return 'kidney_failure'
}

export interface GfrInput {
  method: GfrMethodType | null | undefined
  serumCreatinineMgDl: number | null | undefined
  urineCreatinine?: number | null
  urineVolume?: number | null
  collectionTime?: number | null
  // ms since 1 January 1970; 0 is that date, not a missing one (CY-314)
  birthDate: number | null | undefined
  weight: number | null | undefined
  bsa: number | null | undefined
  sex: string | null | undefined
}

/** The GFR the Add GFR dialog calculates, or undefined when an input the method needs is missing. */
export function calculateGfr(input: GfrInput): number | undefined {
  const {method, serumCreatinineMgDl, urineCreatinine, urineVolume, collectionTime, birthDate, weight, bsa, sex} = input
  const isFemale = sex === Sex.enum.female

  if (method === 'cockcroft_gault' && serumCreatinineMgDl && isDefined(birthDate) && weight && sex) {
    return cockcroftGault(getAge(birthDate), weight, serumCreatinineMgDl, isFemale)
  }

  if (method === 'urinary_24h' && urineCreatinine && serumCreatinineMgDl && urineVolume && collectionTime) {
    return urinary24h(urineCreatinine, urineVolume, serumCreatinineMgDl, collectionTime)
  }

  if (method === 'jelliffe' && serumCreatinineMgDl && isDefined(birthDate) && bsa) {
    return jelliffe(getAge(birthDate), bsa, serumCreatinineMgDl, isFemale)
  }

  return undefined
}
